import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Paper,
  Grid,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import { PhotoCamera, Add as AddIcon } from '@mui/icons-material';
import { Template } from '../../types/template';
import { getFileUrl } from '../../services/api';

interface TemplateFormProps {
  onSubmit: (formData: FormData) => Promise<void>;
  initialData?: Partial<Template>;
}

export const TemplateForm: React.FC<TemplateFormProps> = ({ onSubmit, initialData }) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [prompt, setPrompt] = useState(initialData?.aiTarget?.prompt || '');
  const [negativePrompt, setNegativePrompt] = useState(initialData?.aiTarget?.negativePrompt || '');
  const [category, setCategory] = useState(initialData?.metadata?.category || '');
  const [tags, setTags] = useState<string[]>(initialData?.metadata?.tags || []);
  const [isPublic, setIsPublic] = useState(initialData?.isPublic ?? true);
  const [newTag, setNewTag] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState(getFileUrl(initialData?.imageUrl) || '');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleAddTag = () => {
    if (newTag && !tags.includes(newTag)) {
      setTags([...tags, newTag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData();
    const templateData = {
      title,
      description,
      aiTarget: {
        prompt,
        negativePrompt
      },
      metadata: {
        category,
        tags,
      },
      isPublic
    };

    formData.append('data', JSON.stringify(templateData));
    
    if (selectedImage) {
      formData.append('file', selectedImage);
    }

    await onSubmit(formData);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={handleImageChange}
            />
            <Box display="flex" flexDirection="column" alignItems="center">
              {previewUrl && (
                <Box mb={2}>
                  <img
                    src={previewUrl}
                    alt="Template preview"
                    style={{ maxWidth: '100%', maxHeight: '200px' }}
                  />
                </Box>
              )}
              <Button
                variant="contained"
                startIcon={<PhotoCamera />}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Template Image
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="AI Prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              multiline
              rows={2}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Negative Prompt (Optional)"
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <TextField
                label="Add Tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                size="small"
              />
              <IconButton onClick={handleAddTag} color="primary">
                <AddIcon />
              </IconButton>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  sx={{ m: 0.5 }}
                />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
              }
              label="Make template public"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
            >
              {initialData ? 'Update Template' : 'Create Template'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}; 